import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';

const OTP_RESEND_SECONDS = 60;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const buildRedirect = () => {
    const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
    const pathPrefix = basePath ? `/${basePath}` : '';
    return `${window.location.origin}${pathPrefix}/`;
  };

  const handleSendOtp = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setOtp('');
    setOtpSent(false);
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: buildRedirect(),
        },
      });
      if (err) {
        setError(err.message);
      } else {
        setOtpSent(true);
        setMessage(t('auth.otpSent'));
        setResendIn(OTP_RESEND_SECONDS);
      }
    } catch {
      setError(t('auth.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (otp.length < 6) {
      setError(t('auth.otpInvalid'));
      return;
    }
    setLoading(true);
    try {
      const { data, error: err } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email',
      });
      if (err) {
        setError(err.message);
        return;
      }
      // Save name / phone on the profile only if they are not already set, so
      // we never overwrite an existing user's data on a re-sign-in.
      const user = data?.user;
      const meta: Record<string, unknown> = user?.user_metadata || {};
      const nextMeta: Record<string, string> = {};
      if (name.trim() && !meta.full_name) nextMeta.full_name = name.trim();
      if (phone.trim() && !meta.phone) nextMeta.phone = phone.trim();
      if (Object.keys(nextMeta).length > 0) {
        await supabase.auth.updateUser({ data: nextMeta });
      }
      navigate('/');
    } catch {
      setError(t('auth.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0) return;
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: buildRedirect(),
        },
      });
      if (err) {
        setError(err.message);
      } else {
        setMessage(t('auth.otpSent'));
        setResendIn(OTP_RESEND_SECONDS);
      }
    } catch {
      setError(t('auth.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const changeEmail = () => {
    setOtpSent(false);
    setOtp('');
    setError('');
    setMessage('');
    setResendIn(0);
  };

  const inputClass =
    'w-full text-sm rounded-md border border-secondary-300 px-4 py-3 text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition';

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 flex items-center justify-center px-4 py-24 md:py-28">
        <div className="w-full max-w-[1000px] grid grid-cols-1 lg:grid-cols-2 rounded-2xl overflow-hidden border border-secondary-200">
          {/* Left panel - form */}
          <div className="bg-white p-8 md:p-12 order-2 lg:order-1">
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-secondary-900">
                {t('auth.createAccount')}
              </h1>
              <p className="mt-2 text-sm text-secondary-500">
                {otpSent
                  ? t('auth.otpSentShort')
                  : t('auth.joinUs')}
              </p>
            </div>

            {!otpSent ? (
              <form onSubmit={handleSendOtp} className="space-y-5">
                <div>
                  <label htmlFor="fullname" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('auth.fullName')}
                  </label>
                  <input
                    id="fullname"
                    name="full_name"
                    type="text"
                    required
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('auth.yourFullName')}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="reg-email" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('auth.emailAddress')}
                  </label>
                  <input
                    id="reg-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="reg-phone" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('auth.phoneNumber')}
                  </label>
                  <input
                    id="reg-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+84 ..."
                    className={inputClass}
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                    {error}
                  </p>
                )}
                {message && (
                  <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2.5">
                    {message}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-md px-4 py-3 transition-colors whitespace-nowrap"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" />
                      {t('auth.sendingOtp')}
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line text-base" />
                      {t('auth.sendOtp')}
                    </>
                  )}
                </button>

                <p className="text-xs text-secondary-400 text-center leading-relaxed">
                  {t('auth.terms')}
                </p>

                <p className="mt-6 text-center text-sm text-secondary-500">
                  {t('auth.haveAccount')}{' '}
                  <Link
                    to="/login"
                    className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    {t('auth.signIn')}
                  </Link>
                </p>
              </form>
            ) : (
              <form onSubmit={handleVerify} className="space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="reg-otp" className="block text-sm font-medium text-secondary-700">
                      {t('auth.otpCode')}
                    </label>
                    <button
                      type="button"
                      onClick={changeEmail}
                      className="text-xs font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
                    >
                      {t('auth.changeEmail')}
                    </button>
                  </div>
                  <input
                    id="reg-otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="••••••"
                    className="w-full text-center text-lg tracking-[0.6em] rounded-md border border-secondary-300 px-4 py-3 text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                  />
                  <p className="mt-1.5 text-[11px] text-secondary-400 break-words">{email}</p>
                </div>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                    {error}
                  </p>
                )}
                {message && (
                  <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2.5">
                    {message}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-md px-4 py-3 transition-colors whitespace-nowrap"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" />
                      {t('auth.verifying')}
                    </>
                  ) : (
                    <>
                      <i className="ri-shield-keyhole-line text-base" />
                      {t('auth.verifySignUp')}
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center">
                  {resendIn > 0 ? (
                    <span className="text-xs text-secondary-400">
                      {t('auth.resendIn', { s: resendIn })}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={loading}
                      className="text-xs font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {t('auth.resendOtp')}
                    </button>
                  )}
                </div>

                <p className="mt-6 text-center text-sm text-secondary-500">
                  {t('auth.haveAccount')}{' '}
                  <Link
                    to="/login"
                    className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    {t('auth.signIn')}
                  </Link>
                </p>
              </form>
            )}
          </div>

          {/* Right panel - image */}
          <div className="relative hidden lg:block min-h-[640px] order-1 lg:order-2">
            <img
              src="https://readdy.ai/api/search-image?query=Charming%20ancient%20town%20of%20Hoi%20An%20Vietnam%20at%20dusk%2C%20colorful%20traditional%20lanterns%20glowing%20warm%20along%20the%20quiet%20river%2C%20old%20yellow%20colonial%20buildings%20and%20wooden%20boats%20reflecting%20on%20the%20water%2C%20soft%20evening%20light%20with%20a%20magical%20atmosphere%2C%20cinematic%20travel%20photography%2C%20rich%20warm%20color%20palette%2C%20dreamy%20serene%20mood%2C%20ultra%20detailed&width=1000&height=1400&seq=register-vietnam-01&orientation=portrait"
              alt={t('auth.registerAlt')}
              title={t('auth.registerTitle')}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/20" />
            <div className="relative h-full flex flex-col justify-end p-10 text-white">
              <span className="font-heading text-3xl font-bold leading-tight">
                {t('auth.registerHero')}
              </span>
              <p className="mt-3 text-sm text-white/80 leading-relaxed">
                {t('auth.registerHeroText')}
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}